import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TextField, InputAdornment,
  Skeleton, Avatar, Chip, FormControl, InputLabel, Select, MenuItem, useTheme,
  alpha, Tooltip, IconButton
} from '@mui/material';
import { keyframes } from '@mui/system';
import { Add, Search, Business, OpenInNew, Refresh } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { schoolService, School as SchoolType } from '../services/schoolService';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { toUploadUrl } from '../utils/uploadUrl';
import CreateSchoolDialog from '../components/schools/CreateSchoolDialog';
import SchoolDrawer from '../components/schools/SchoolDrawer';

// School logos are stored in the DB as bare filenames (e.g.
// `logos-17712638749793-…svg`) — *not* paths. Single-arg toUploadUrl would
// build `<host>/logos-…svg` which 404s. Route bare names through the two-arg
// form so they hit `/uploads/logos/<file>`.
const resolveSchoolLogo = (logo?: string | null): string | undefined => {
  if (!logo) return undefined;
  if (/^https?:\/\//i.test(logo)) return logo;
  if (logo.startsWith('/')) return toUploadUrl(logo);
  return toUploadUrl('logos', logo);
};

// Skeleton-style shimmer sweep used as a non-blocking refresh indicator.
// Same pattern as MeritTab / ApplicationsTab so filter/search refetches don't
// flash the whole table.
const tableShimmerKf = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(200%); }
`;

type StatusFilter = '' | 'true' | 'false';

const SchoolsPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { user, loading: authLoading } = useAuth();

  // List state
  const [schools, setSchools] = useState<SchoolType[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');

  // Loading / shimmer
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const firstLoadRef = useRef(true);

  // Dialogs / drawer
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerSchoolId, setDrawerSchoolId] = useState<string | null>(null);

  const isSystemAdmin = !!user?.isSystemAdmin;

  const load = useCallback(async () => {
    if (authLoading) return;
    if (!isSystemAdmin) {
      enqueueSnackbar('Access denied. Only Super Admin can manage schools.', { variant: 'error' });
      return;
    }
    if (firstLoadRef.current) setLoading(true);
    else setRefreshing(true);
    try {
      const params: any = { page: page + 1, limit: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter === 'true') params.isActive = true;
      else if (statusFilter === 'false') params.isActive = false;
      const res = await schoolService.getSchools(params);
      setSchools(res.schools);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to load schools', { variant: 'error' });
    } finally {
      firstLoadRef.current = false;
      setLoading(false);
      setRefreshing(false);
    }
  }, [authLoading, isSystemAdmin, page, rowsPerPage, debouncedSearch, statusFilter, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);

  // Reset page when filters change so the user never lands on a stale page.
  useEffect(() => { setPage(0); }, [debouncedSearch, statusFilter]);

  const openSchool = useCallback((id: string) => setDrawerSchoolId(id), []);
  const closeDrawer = useCallback(() => setDrawerSchoolId(null), []);

  const totalPagesLabel = useMemo(() => `${totalCount} school${totalCount === 1 ? '' : 's'}`, [totalCount]);

  return (
    <Box>
      {/* Page header — matches StudentsPage chrome */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 3,
          gap: 2
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} color="text.primary">
            Schools
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage every school on the platform — onboard, view admins, and review subscriptions.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
          sx={{ borderRadius: 2 }}
        >
          Add School
        </Button>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{ p: 2, mb: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 7, md: 8 }}>
            <TextField
              fullWidth size="small"
              placeholder="Search by name, code, or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" color="action" />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid size={{ xs: 8, sm: 3, md: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Active</MenuItem>
                <MenuItem value="false">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 4, sm: 2, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={() => load()} disabled={refreshing || loading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Schools table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden' }}>
        <TableContainer sx={{ position: 'relative' }}>
          {/* Shimmer scoped to rows only — header titles never animate. */}
          {refreshing && !loading && (
            <Box
              sx={{
                position: 'absolute', top: 53, left: 0, right: 0, bottom: 0,
                overflow: 'hidden', pointerEvents: 'none', zIndex: 2,
                '&::before': {
                  content: '""',
                  position: 'absolute', top: 0, bottom: 0, left: 0,
                  width: '40%',
                  background: `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.10)}, transparent)`,
                  animation: `${tableShimmerKf} 1.4s infinite linear`
                }
              }}
            />
          )}
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>School</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Users</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Students</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7}><Skeleton variant="rounded" height={160} /></TableCell></TableRow>
              ) : schools.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {debouncedSearch || statusFilter ? 'No schools match your filters.' : 'No schools yet. Add your first one to get started.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : schools.map(s => (
                <TableRow
                  key={s._id}
                  hover
                  onClick={() => openSchool(s._id)}
                  sx={{ cursor: 'pointer', '& td': { borderBottom: 1, borderColor: 'divider' } }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={resolveSchoolLogo(s.logo)}
                        variant="rounded"
                        sx={{
                          width: 36, height: 36,
                          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.1),
                          color: 'primary.main',
                          fontSize: 14, fontWeight: 700,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
                        }}
                      >
                        {s.name ? s.name.split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') : <Business fontSize="small" />}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>{s.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{s.email}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={s.code || '—'}
                      size="small"
                      sx={{
                        fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.3,
                        bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
                        color: 'primary.main',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={s.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={s.isActive ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{s.userCount ?? 0}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600}>{s.studentCount ?? 0}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(s.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Open">
                      <IconButton size="small" onClick={() => openSchool(s._id)}>
                        <OpenInNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
        />
      </Paper>

      {/* Subtle row count caption for clarity. */}
      {!loading && schools.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {totalPagesLabel}
        </Typography>
      )}

      <CreateSchoolDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      <SchoolDrawer
        open={!!drawerSchoolId}
        schoolId={drawerSchoolId}
        onClose={closeDrawer}
        onChanged={load}
      />
    </Box>
  );
};

export default SchoolsPage;
